import { EventDrivenStore } from '@core/store/eventDrivenStore';
import { computed } from 'vue';
import constants from '@core/utils/constants';
import {
  DELIVERYBOOK_TABS,
  DELIVERYBOOK_STATUS
} from './constants.js';

/**
 * DeliveryBookStore V3
 * Gestion complete de l'etat du carnet d'adresses avec EventDrivenStore
 */
class DeliveryBookStore extends EventDrivenStore {
  constructor() {
    super('deliverybook', {
      // Addresses state
      addresses: [],
      savedAddresses: [],
      invitations: [],
      requests: [],

      // UI state
      selectedTab: DELIVERYBOOK_TABS.SAVED,
      searchQuery: '',
      loading: false,
      error: null,

      // Modal state
      isModalOpen: false,
      isFromCart: false,
      requestKey: null,

      // Forms state
      addRequestVisible: false,
      fabExpanded: false
    });

    console.log('[DeliveryBookStore] Initializing deliverybook store V3');
  }

  setupEvents() {
    const bus = this.bus;
    if (!bus) {
      console.warn('[DeliveryBookStore] Event bus not available');
      return;
    }

    // Addresses loaded
    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESSES_LOADED, (data) => {
      // Prefer pre-categorized data if available
      if (data.saved && data.invitations && data.requests) {
        this.state.addresses = data.addresses || [];
        this.state.savedAddresses = data.saved;
        this.state.invitations = data.invitations;
        this.state.requests = data.requests;
      } else {
        // Fallback to re-categorization
        const saved = [];
        const invitations = [];
        const requests = [];

        if (Array.isArray(data.addresses)) {
          data.addresses.forEach(addr => {
            if (addr.invitationLink) invitations.push(addr);
            else if (addr.locationRequestLink) requests.push(addr);
            else saved.push(addr);
          });
        }

        this.state.addresses = data.addresses || [];
        this.state.savedAddresses = saved;
        this.state.invitations = invitations;
        this.state.requests = requests;
      }

      this.state.loading = false;
      this.state.error = null;
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESSES_LOAD_ERROR, (error) => {
      console.error('[DeliveryBookStore] ADDRESSES_LOAD_ERROR received', error);
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESSES_LOADING, () => {
      console.log('[DeliveryBookStore] ADDRESSES_LOADING received');
      this.state.loading = true;
    });

    // Address created
    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATED, (data) => {
      console.log('[DeliveryBookStore] ADDRESS_CREATED received', data);
      if (data.address) {
        this.state.savedAddresses.unshift(data.address);
        this.state.addresses.unshift(data.address);
      }
      this.state.loading = false;
      this.state.error = null;

      // Emit legacy event
      bus.emit(constants.EVENTS.DELIVERYBOOK.NEW_DELIVERY_LOCATION_ADDED);
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATE_ERROR, (error) => {
      console.error('[DeliveryBookStore] ADDRESS_CREATE_ERROR received', error);
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATING, () => {
      console.log('[DeliveryBookStore] ADDRESS_CREATING received');
      this.state.loading = true;
    });

    // Address updated
    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_UPDATED, (data) => {
      console.log('[DeliveryBookStore] ADDRESS_UPDATED received', data);
      if (data.address) {
        const index = this.state.savedAddresses.findIndex(a => a.id === data.address.id);
        if (index !== -1) {
          this.state.savedAddresses[index] = data.address;
        }
        const allIndex = this.state.addresses.findIndex(a => a.id === data.address.id);
        if (allIndex !== -1) {
          this.state.addresses[allIndex] = data.address;
        }
      }
      this.state.loading = false;
      this.state.error = null;

      // Emit legacy event
      bus.emit(constants.EVENTS.DELIVERYBOOK.DELIVERY_LOCATION_LIST_CHANGED);
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_UPDATE_ERROR, (error) => {
      console.error('[DeliveryBookStore] ADDRESS_UPDATE_ERROR received', error);
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_UPDATING, () => {
      console.log('[DeliveryBookStore] ADDRESS_UPDATING received');
      this.state.loading = true;
    });

    // Address deleted
    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_DELETED, (data) => {
      console.log('[DeliveryBookStore] ADDRESS_DELETED received', data);
      const addressId = data.addressId;

      this.state.savedAddresses = this.state.savedAddresses.filter(a => a.id !== addressId);
      this.state.invitations = this.state.invitations.filter(a => a.id !== addressId);
      this.state.requests = this.state.requests.filter(a => a.id !== addressId);
      this.state.addresses = this.state.addresses.filter(a => a.id !== addressId);
      this.state.loading = false;
      this.state.error = null;

      // Emit legacy event
      bus.emit(constants.EVENTS.DELIVERYBOOK.DELIVERY_LOCATION_LIST_CHANGED);
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_DELETE_ERROR, (error) => {
      console.error('[DeliveryBookStore] ADDRESS_DELETE_ERROR received', error);
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_DELETING, () => {
      console.log('[DeliveryBookStore] ADDRESS_DELETING received');
      this.state.loading = true;
    });

    // Address request created
    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATED, (data) => {
      console.log('[DeliveryBookStore] ADDRESS_REQUEST_CREATED received', data);
      if (data.request) {
        this.state.requests.unshift(data.request);
        this.state.addresses.unshift(data.request);
      }
      this.state.loading = false;
      this.state.error = null;
      console.log('[DeliveryBookStore] Updated state after request creation:', this.state.requests.length);
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATE_ERROR, (error) => {
      console.error('[DeliveryBookStore] ADDRESS_REQUEST_CREATE_ERROR received', error);
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATING, () => {
      console.log('[DeliveryBookStore] ADDRESS_REQUEST_CREATING received');
      this.state.loading = true;
    });

    // Address invite created
    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_INVITE_CREATED, (data) => {
      console.log('[DeliveryBookStore] ADDRESS_INVITE_CREATED received', data);
      if (data.invite) {
        this.state.invitations.unshift(data.invite);
        this.state.addresses.unshift(data.invite);
      }
      this.state.loading = false;
      this.state.error = null;
      console.log('[DeliveryBookStore] Updated state after invite creation:', this.state.invitations.length);
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_INVITE_CREATE_ERROR, (error) => {
      console.error('[DeliveryBookStore] ADDRESS_INVITE_CREATE_ERROR received', error);
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_INVITE_CREATING, () => {
      console.log('[DeliveryBookStore] ADDRESS_INVITE_CREATING received');
      this.state.loading = true;
    });

    // Address invite deleted — réutilise ADDRESS_DELETED qui filtre déjà les 3 listes

    // Modal events
    bus.on(constants.EVENTS.DELIVERYBOOK.MODAL_OPEN, (data) => {
      console.log('[DeliveryBookStore] MODAL_OPEN received', data);
      this.state.isModalOpen = true;
      this.state.isFromCart = data?.isFromCart || false;
      this.state.requestKey = data?.requestKey || null;
    });

    bus.on(constants.EVENTS.DELIVERYBOOK.MODAL_CLOSE, () => {
      console.log('[DeliveryBookStore] MODAL_CLOSE received');
      this.state.isModalOpen = false;
      this.state.isFromCart = false;
      this.state.requestKey = null;
    });

    // Tab change
    bus.on(constants.EVENTS.DELIVERYBOOK.TAB_CHANGED, (tab) => {
      console.log('[DeliveryBookStore] TAB_CHANGED received', tab);
      this.state.selectedTab = tab;
    });

    console.log('[DeliveryBookStore] All event listeners set up successfully');
  }

  // ============================================
  // GETTERS
  // ============================================
  
  get currentTabAddresses() {
    const storeState = this.state;
    switch (storeState.selectedTab) {
      case DELIVERYBOOK_TABS.SAVED:
        return storeState.savedAddresses;
      case DELIVERYBOOK_TABS.INVITATIONS:
        return storeState.invitations;
      case DELIVERYBOOK_TABS.REQUESTS:
        return storeState.requests;
      default:
        return [];
    }
  }

  get filteredAddresses() {
    const source = this.currentTabAddresses;
    const query = this.state.searchQuery?.toLowerCase().trim() || '';
    
    if (!query) return source;

    return source.filter(addr => {
      const searchableText = [
        addr.name || '',
        addr.locationText || '',
        addr.phoneNumber || '',
        addr.pseudo || '',
        addr.location?.address || '',
        addr.location?.city || ''
      ].join(' ').toLowerCase();

      return searchableText.includes(query);
    });
  }

  get savedCount() { return this.state.savedAddresses.length; }
  get invitationsCount() { return this.state.invitations.length; }
  get requestsCount() { return this.state.requests.length; }

  get isLoading() { return this.state.loading; }
  get hasError() { return this.state.error !== null; }
  get errorMessage() { return this.state.error; }

  get isModalOpen() { return this.state.isModalOpen; }
  get isFromCart() { return this.state.isFromCart; }

  get isCurrentTabEmpty() {
    return this.currentTabAddresses.length === 0;
  }

  get hasAnyAddress() {
    return this.state.addresses.length > 0;
  }

  // Actions (methods that mutate state)
  get actions() {
    const store = this;

    return {
      // Addresses
      setAddresses(addresses) {
        store.state.addresses = addresses;
      },

      // Add address
      addAddress(address) {
        store.state.savedAddresses.unshift(address);
        store.state.addresses.unshift(address);
      },

      // Update address
      updateAddress(address) {
        const index = store.state.savedAddresses.findIndex(a => a.id === address.id);
        if (index !== -1) {
          store.state.savedAddresses[index] = address;
        }
        const allIndex = store.state.addresses.findIndex(a => a.id === address.id);
        if (allIndex !== -1) {
          store.state.addresses[allIndex] = address;
        }
      },

      // Delete address
      deleteAddress(addressId) {
        store.state.savedAddresses = store.state.savedAddresses.filter(a => a.id !== addressId);
        store.state.invitations = store.state.invitations.filter(a => a.id !== addressId);
        store.state.requests = store.state.requests.filter(a => a.id !== addressId);
        store.state.addresses = store.state.addresses.filter(a => a.id !== addressId);
      },

      // Search
      setSearchQuery(query) {
        store.state.searchQuery = query;
      },

      // Tab
      setSelectedTab(tab) {
        console.log('[DeliveryBookStore] Setting selected tab:', tab);
        store.state.selectedTab = tab;
      },

      // Modal
      openModal(data = {}) {
        store.state.isModalOpen = true;
        store.state.isFromCart = data.isFromCart || false;
        store.state.requestKey = data.requestKey || null;
      },

      closeModal() {
        store.state.isModalOpen = false;
        store.state.isFromCart = false;
        store.state.requestKey = null;
      },

      // FAB
      toggleFab() {
        store.state.fabExpanded = !store.state.fabExpanded;
      },

      closeFab() {
        store.state.fabExpanded = false;
      },

      openRequestForm() {
        store.state.addRequestVisible = true;
        store.state.fabExpanded = false;
      },

      closeRequestForm() {
        store.state.addRequestVisible = false;
      },

      // UI
      setLoading(loading) {
        store.state.loading = loading;
      },

      setError(error) {
        store.state.error = error;
      },

      clearError() {
        store.state.error = null;
      }
    };
  }
}

export default DeliveryBookStore;
